import React, { useRef, useState, useEffect } from "react"
import { Formik, Form } from "formik"
import {
    Button,
    DialogTitle,
    Dialog,
    DialogContent,
    DialogActions,
    TextField,
    styled,
    FormControl,
    MenuItem,
    FormHelperText,
} from "@mui/material"
import Select from "@mui/material/Select"
import InputLabel from "@mui/material/InputLabel"
import { useSelector } from "react-redux"
import { getMachineModelsSelector } from "../../../../../reducers/Machines/machines"
import { kitchenAreaMachinesSchema } from "../../../../../utils/yupValidators"
import { checkCookingMachine } from "../../../../../utils/machinesutils"


const getInitialValues = (machine) => {
    if (machine) {
        // Handle both cases: linkedMachine as object or as string (objectId)
        const linkedMachineId = typeof machine.linkedMachine === "string" ? machine.linkedMachine : machine.linkedMachine?.objectId || ""
        return {
            name: machine.name,
            machineModel: machine.machineModel.objectId,
            linkedMachine: linkedMachineId
        }
    }

    return {
        name: "",
        linkedMachine: ""
    }
}

const StyledDialog = styled(Dialog)({
    "& .MuiPaper-root": {
        width: 500,
        padding: 10
    },
})

export const MachineKitchenAreaModal = ({ onClose, open, onConfirm, machine, coolingCellMachines, machineTypes }) => {
    const machineModels = useSelector(getMachineModelsSelector)
    const formikRef = useRef(null)
    const [isCookingMachine, setIsCookingMachine] = useState(false)

    // Check if the initial machine is a cooking machine
    useEffect(() => {
        if (machine && machine.machineModel) {
            const isCooking = checkCookingMachine(machine.machineModel.objectId, machineTypes)
            setIsCookingMachine(isCooking)
        } else setIsCookingMachine(false)
    }, [machine, machineTypes])

    const handleConfirm = () => {
        formikRef.current.submitForm()
    }

    const handleSubmit = (values) => {
        const machineModel = machineModels.find(machineModel => machineModel.objectId === values.machineModel)
        
        // Build newValues with linkedMachine properly formatted
        const newValues = { 
            name: values.name,
            machineModel,
            linkedMachine: values.linkedMachine ? values.linkedMachine : undefined
        }
        onConfirm(newValues)
        onClose()
    }

    const handleCancel = () => {
        onClose()
    }

    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title" sx={{ pb: 0 }}>
                Ajouter une machine
            </DialogTitle>
            <DialogContent sx={{ mt: 1 }}>
                <Formik
                    innerRef={formikRef}
                    initialValues={getInitialValues(machine)}
                    validationSchema={kitchenAreaMachinesSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, handleChange, handleBlur, errors }) => {
                        return (
                            <Form>
                                <TextField
                                    variant="standard"
                                    label="Nom de la machine"
                                    name="name"
                                    onChange={(value) => {
                                        handleChange(value)
                                    }}
                                    onBlur={handleBlur}
                                    value={values.name}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    fullWidth
                                />
                                <FormControl variant="standard" fullWidth sx={{ mt: 2.5 }} error={!!errors.machineModel}>
                                    <InputLabel id="machineModel-label">Modèle</InputLabel>
                                    <Select
                                        variant="standard"
                                        name="machineModel"
                                        value={values.machineModel}
                                        onChange={(value) => {
                                            handleChange(value)
                                            setIsCookingMachine(checkCookingMachine(value.target.value, machineTypes))
                                        }}
                                        error={!!errors.machineModel}
                                        helperText={errors.machineModel}
                                        fullWidth
                                    >
                                        {machineModels.map((machineModel, index) => (
                                            <MenuItem key={index} value={machineModel.objectId}>
                                                {machineModel.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.machineModel && <FormHelperText>{errors.machineModel}</FormHelperText>}
                                </FormControl>
                                {isCookingMachine &&
                                    <FormControl variant="standard" fullWidth sx={{ mt: 2.5 }} error={!!errors.linkedMachine}>
                                        <InputLabel id="linkedMachine-label">Cellule de refroidissement reliée</InputLabel>
                                        <Select
                                            variant="standard"
                                            name="linkedMachine"
                                            value={values.linkedMachine}
                                            onChange={(value) => {
                                                handleChange(value)
                                            }}
                                            error={!!errors.linkedMachine}
                                            helperText={errors.linkedMachine}
                                            fullWidth
                                        >
                                            <MenuItem key={"noLinkedMachine"} value="">
                                                Aucune
                                            </MenuItem>
                                            {coolingCellMachines.map((machine, index) => (
                                                <MenuItem key={index} value={machine.objectId}>
                                                    {machine.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.linkedMachine && <FormHelperText>{errors.linkedMachine}</FormHelperText>}
                                    </FormControl>
                                }
                            </Form>
                        )
                    }}
                </Formik>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
                <Button onClick={handleCancel} color="primary">
                    Annuler
                </Button>
                <Button onClick={handleConfirm} color="primary" variant="contained">
                    Enregistrer
                </Button>
            </DialogActions>
        </StyledDialog>
    )
}

export default MachineKitchenAreaModal